// src/encounter/allied-health-plan-view.tsx
// PROD-2040 — the allied-health PLAN step surface. The THIRD role wrapper on the encounter step
// pattern, beside the physician's A/P (assessment-plan-view.tsx) and the nurse's flow, which drops
// A/P entirely. Purely additive: neither of those two is touched by this file, and neither imports
// it.
//
// WHAT MAKES IT DIFFERENT FROM A/P, and why it is not A/P with a flag. A/P is an
// assessment-AND-ORDERS canvas: diagnosis cards, per-type order sections, drag-to-attach, a
// medication dose editor. This surface has none of that, because allied health does not prescribe.
// It renders a PLAN and terminates there — `ALLIED_PLAN_TERMINAL`. There is no `onAddOrder`, no
// `allowedOrderTypes`, no `onAttachOrder`, and no picker for one: not disabled, ABSENT. A disabled
// prescribe control on a surface whose role can never prescribe is the anti-fiction failure this
// repo already ruled against (see the header of encounter-section-chrome.tsx) — it teaches the
// clinician to expect a capability that is not coming.
//
// ONE STEP, ONE ROLE, SPECIALITY-DRIVEN CONTENT. The discipline is a SPECIALITY, so it varies the
// framework block inside this one step rather than forking the step, the role, or this component.
//
// WHEN THE SPECIALITY DOES NOT RESOLVE, THE CLINICIAN IS ASKED (review response — this replaced a
// "show all three frameworks" fail-open). `speciality` is unresolved for every current user, so
// showing everything was the permanent state dressed as an edge case. The framework picker costs
// one interaction and makes the unresolved state visible and intentional. See `resolveAlliedPlan`.
//
// THE PICKED FRAMEWORK IS SAVED WITH THE FIELDS ([PROD-2949], MS-1). The host now supplies
// `alliedPlan` from `useNonPrescriberPlanDraft` (behind `?ef=planSave`): the selection is lifted to
// the host as `framework` + `onFrameworkChange`, alongside the field values, and stored with them in
// one request — exactly the motion this header used to ask for. Without `onFrameworkChange` (stories,
// the flag off) the pick stays component state and does not survive a remount, as before.
//
// Presentational: no fetch, no store read. Values and handlers flow in via props, the same seam
// A/P and the wrap-up view use.
import { useId, useState, type ReactNode } from 'react';
import { QuestionLabel, RadioGroup, SectionEyebrow, TEXT_ROLE_CLASSES, TextArea, TextField } from '@scripsteam/scrips-signal-ds';
import { useDesignV2 } from './lib/design-v2';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import { PlanSaveLine, PlanSaveNotice } from './plan-save-status';
import type { PlanSaveState } from './use-non-prescriber-plan-draft';
import {
  ALLIED_PLAN_TERMINAL,
  FRAMEWORK_CHOICES,
  allFrameworkSections,
  alliedPlanFor,
  planSections,
  resolveAlliedPlan,
  type AlliedPlanFramework,
  type AlliedPlanPickReason,
  type AlliedPlanSection,
  type AlliedPlanSectionBase,
} from './lib/allied-health-plan';

export interface AlliedHealthPlanViewProps {
  /**
   * Which allied-health discipline is documenting.
   *
   * A plain `string` because that is what the wire carries — an arbitrary value, not a member of
   * `AlliedSpeciality`. Today it is ALWAYS absent: `speciality` is not on `UserData` and the
   * backend returns it empty for staff accounts (see the follow-up note on `clinicalRoleFor` in
   * src/auth/use-role.ts). Absent → the framework picker. Present but unrecognised → the picker
   * too, with a DIFFERENT notice, because that is a data problem rather than the expected state.
   */
  speciality?: string | null;
  /** Current answers, keyed by `AlliedPlanField.id`. */
  values?: Readonly<Record<string, string>>;
  /** Persist one field. Omitted → the surface renders read-only (a signed/locked encounter). */
  onChange?: (fieldId: string, value: string) => void;
  /** A signed or locked encounter — the plan is a record at that point, not a form. */
  readOnly?: boolean;
  /**
   * [PROD-2949] The saved framework, when the host persists the pick. Read only while the speciality
   * does not resolve (a resolved speciality decides the framework). Paired with `onFrameworkChange`.
   */
  framework?: AlliedPlanFramework | null;
  /** [PROD-2949] Persist the pick. Supplied → the picker is controlled by `framework`. */
  onFrameworkChange?: (framework: AlliedPlanFramework) => void;
  /** [PROD-2949] The plan's own save state (it saves apart from the note), drawn inside the FIRST
   *  card: the save line at its header right, a refusal as a Banner on top. Absent → neither. */
  saveState?: PlanSaveState;
  /**
   * Frame v2 only: the plan is drawn inside the note's "Today's plan" block. No card frames (a card in a
   * card read as a "Plan" block in the "Plan" section, the independent review, 6 Oct): the spine — the
   * part the block is named for — draws no title of its own, and the framework picker and the
   * framework's own section keep their names as eyebrows. Absent → the cards exactly as before.
   */
  inNote?: boolean;
  /**
   * [PROD-3376] The served template already draws the nutrition care process (it carries an item
   * coded LOINC 75305-3). With the PES-ADIME framework that makes the free-text "Nutrition care
   * process" card a second copy, so the view draws only the spine and no picker — UNLESS a pes-* /
   * adime-* field already holds saved text, which is never hidden. Absent → exactly as before.
   */
  frameworkInTemplate?: boolean;
  /**
   * [PROD-3376] The served template draws its own Plan sections (coded LOINC 18776-5, lib/allied-soap.ts),
   * whatever the discipline: the framework card would ask the same things again, so the view draws only the
   * spine and no picker — UNLESS a framework field already holds saved text, which is never hidden.
   */
  templateOwnsPlan?: boolean;
}

/** [PROD-3376] Does any framework card field (PES-ADIME, goals and barriers, FITT-VP) already hold text? */
export function hasSavedFrameworkText(values: Readonly<Record<string, string>> | undefined): boolean {
  return Object.entries(values ?? {}).some(
    ([id, v]) => /^(pes|adime|goals|fitt)-/.test(id) && typeof v === 'string' && v.trim() !== '',
  );
}

/** [PROD-3376] Is any field of the PES-ADIME card already holding text? Saved clinical text is never hidden. */
export function hasSavedPesAdimeText(values: Readonly<Record<string, string>> | undefined): boolean {
  return Object.entries(values ?? {}).some(
    ([id, v]) => (id.startsWith('pes-') || id.startsWith('adime-')) && typeof v === 'string' && v.trim() !== '',
  );
}

const WRAP = 'flex flex-col gap-6 w-full';
/** In the note (frame v2): no card frame — the NoteBlock is the card. */
const SECTION_IN_NOTE = 'flex flex-col gap-3';

const SECTION =
  'flex flex-col gap-4 p-6 rounded-lg ' +
  'border border-border-default bg-surface-card';

/** Design sheet v1.1 §1 Surfaces (switch ON only): the card lifts — card border + `--shadow-2`. */
const SECTION_V2 =
  'flex flex-col gap-4 p-6 rounded-lg ' +
  'border border-border-card bg-surface-card shadow-2';

const SECTION_TITLE = 'text-md font-bold text-text-primary';
/** ON: a section title is the DS `panel-title` role (18 / semibold, text-primary). */
const SECTION_TITLE_V2 = TEXT_ROLE_CLASSES['panel-title'];

/** Card subtitle. `text-secondary`, not `text-hint` (design desk, 5 Oct). Measured with getComputedStyle
 *  on the real card in frame v2: text-hint rgb(175,186,192) on rgb(255,255,255) = 1.98:1, under the
 *  4.5:1 a 12px line needs; text-secondary rgb(90,107,117) = 5.53:1. */
const SECTION_SUBTITLE = 'text-xs text-text-secondary';

/** A card header: the title stack on the left, the plan's save line on the right (as the note header). */
const SECTION_HEADER = 'flex flex-row items-start justify-between gap-4';

const FIELDS = 'flex flex-col gap-4';

const STACK_1 = 'flex flex-col gap-1';

/** Why the picker is on screen, in words for the clinician. */
const PICK_PROMPT: Record<AlliedPlanPickReason, string> = {
  // The expected state today, so it reads as a normal question rather than a fault.
  'speciality-absent': 'Your speciality is not recorded yet, so choose the framework this plan follows.',
  // A DIFFERENT line on purpose: this one means the field IS populated, with a discipline this app
  // has no framework for. The clinician can carry on, and the wording makes the data problem
  // reportable instead of silent. The raw value is deliberately not echoed to the screen.
  'speciality-unrecognised':
    'Your recorded speciality is not one this step recognises, so choose the framework this plan follows.',
};

const RADIO_OPTIONS = FRAMEWORK_CHOICES.map((c) => ({ value: c.framework, label: c.label }));
/** The Nutrition care process block's field ids: an answer in any of them means the note was written with it (`planNoNcp`). */
const NCP_FIELD_IDS = allFrameworkSections().find((s) => s.framework === 'pes-adime')?.fields.map((f) => f.id) ?? [];

/** The card frame shared by every plan card: an optional notice on top, then the header row. */
function PlanCard({
  titleId,
  title,
  subtitle,
  notice,
  headerEnd,
  children,
  inNote = false,
  untitled = false,
  ...rest
}: {
  titleId: string;
  title: string;
  subtitle: ReactNode;
  notice?: ReactNode;
  headerEnd?: ReactNode;
  children: ReactNode;
  /** Frame v2: inside a note block — no card frame; the title is an eyebrow. */
  inNote?: boolean;
  /** Frame v2: the block is named for this card, so its title is not drawn (it names the region). */
  untitled?: boolean;
} & Record<`data-${string}`, string | undefined>) {
  const designV2 = useDesignV2();
  const frame = inNote ? SECTION_IN_NOTE : designV2 ? SECTION_V2 : SECTION;
  return (
    <section {...(untitled ? { 'aria-label': title } : { 'aria-labelledby': titleId })} className={frame} {...rest}>
      {notice}
      <div className={SECTION_HEADER}>
        <div className={STACK_1}>
          {untitled ? null : inNote ? (
            <SectionEyebrow as="h3" id={titleId}>
              {title}
            </SectionEyebrow>
          ) : (
            <span id={titleId} className={designV2 ? SECTION_TITLE_V2 : SECTION_TITLE}>
              {title}
            </span>
          )}
          <span className={SECTION_SUBTITLE}>{subtitle}</span>
        </div>
        {headerEnd ? <div className="shrink-0">{headerEnd}</div> : null}
      </div>
      {children}
    </section>
  );
}

/**
 * One plan section as a card of fields. Shared with the nurse's Nursing plan ([PROD-2949]).
 * `notice` (a refusal Banner) sits at the top of the card and `headerEnd` (the save line) at the
 * header's right — status and errors live inside the card (design desk, 5 Oct).
 *
 * ONE LABEL STYLE. Every field's label is the DS `QuestionLabel` in its default seat — the one
 * TextArea draws. TextField's own `label` keeps the Figma "Input" seat (13px bold, text-primary),
 * which made the single-line "Review" fields read bold black beside grey text-area labels; so the
 * single-line field is drawn with the same QuestionLabel, tied to the input by id.
 */
export function PlanSectionBlock({
  section,
  values,
  onChange,
  locked,
  notice,
  headerEnd,
  inNote,
  untitled,
}: {
  section: AlliedPlanSectionBase;
  values?: Readonly<Record<string, string>>;
  onChange?: (fieldId: string, value: string) => void;
  locked: boolean;
  notice?: ReactNode;
  headerEnd?: ReactNode;
  /** Frame v2: inside a note block (no card frame). */
  inNote?: boolean;
  /** Frame v2: the block is named for this section, so its title is not drawn. */
  untitled?: boolean;
}) {
  // Instance-scoped ids, so two copies of a card (a Storybook docs page, a comparison view) never
  // bind a label to the other copy's input.
  const uid = useId();
  const idFor = (fieldId: string) => `${uid}-${section.id}-${fieldId}`;
  return (
    <PlanCard
      titleId={`${uid}-${section.id}-title`}
      title={section.title}
      subtitle={section.subtitle}
      notice={notice}
      headerEnd={headerEnd}
      inNote={inNote}
      untitled={untitled}
    >
      <div className={FIELDS}>
        {section.fields.map((field) =>
          field.multiline ? (
            <TextArea
              key={field.id}
              label={field.label}
              helperText={field.hint}
              value={values?.[field.id] ?? ''}
              readOnly={locked}
              disabled={locked}
              autoGrow
              rows={3}
              onChange={(e) => onChange?.(field.id, e.currentTarget.value)}
            />
          ) : (
            <div key={field.id} className="flex flex-col gap-1 w-full">
              <QuestionLabel htmlFor={idFor(field.id)}>{field.label}</QuestionLabel>
              <TextField
                id={idFor(field.id)}
                helperText={field.hint}
                value={values?.[field.id] ?? ''}
                readOnly={locked}
                disabled={locked}
                // Frame v2 in the note (signal-ds 2.20, FV2-12): a read-only answer is text, "Not asked" when empty.
                {...(inNote && locked ? { readOnlyDisplay: 'text' as const } : {})}
                onChange={(e) => onChange?.(field.id, e.currentTarget.value)}
              />
            </div>
          ),
        )}
      </div>
    </PlanCard>
  );
}

export function AlliedHealthPlanView({
  speciality,
  values,
  onChange,
  readOnly,
  framework: savedFramework,
  onFrameworkChange,
  saveState,
  inNote = false,
  frameworkInTemplate = false,
  templateOwnsPlan = false,
}: AlliedHealthPlanViewProps) {
  const resolution = resolveAlliedPlan(speciality);
  // Component state when the host does not persist the pick; the host's `framework` when it does
  // ([PROD-2949]) — see the file header.
  const [localPick, setLocalPick] = useState<AlliedPlanFramework | null>(null);
  const controlled = onFrameworkChange !== undefined;
  const picked = controlled ? (savedFramework ?? null) : localPick;
  const setPicked = controlled ? onFrameworkChange : setLocalPick;

  // No handler = nothing to write into, so the surface is a record rather than a form. Same shape
  // as the read-only gate on the other step surfaces.
  const locked = readOnly === true || onChange === undefined;

  // The picker shows only while the speciality is unresolved. It STAYS visible after a choice, so
  // a wrong pick is corrected by re-selecting — no extra button, and nothing to undo.
  const needsPick = resolution.kind === 'needs-framework';
  // `?x=plan-no-ncp` (dev default, Antonin 3 Nov): the dietitian's ADIME is asked once, in her template, so the plan does not
  // offer "Nutrition care process". A note that was SAVED with it (the saved framework, or ADIME answers in its values) keeps
  // it. The stored plan can arrive after mount, so this latches ON whenever the saved data shows it and never turns off again:
  // a later pick does not take the option away, so a wrong pick can be picked again. With the option hidden, a clinician cannot
  // pick it, so only stored data can set it.
  const showsNcp = savedFramework === 'pes-adime' || NCP_FIELD_IDS.some((id) => (values?.[id] ?? '').trim() !== '');
  const [savedNcp, setSavedNcp] = useState(showsNcp);
  if (showsNcp && !savedNcp) setSavedNcp(true);
  const noNcp = isEncounterFlagOn('planNoNcp') && !savedNcp;
  const options = noNcp ? RADIO_OPTIONS.filter((o) => o.value !== 'pes-adime') : RADIO_OPTIONS;
  const resolved = resolution.kind === 'resolved' && !(noNcp && resolution.framework === 'pes-adime') ? resolution.framework : null;
  const framework = resolution.kind === 'resolved' ? resolved : picked;
  // The spine, from whichever arm resolved. Both arms carry the SAME underlying spine object, so
  // this is not a second source of truth for it.
  const spine = resolution.kind === 'resolved' ? resolution.plan.spine : resolution.spine;
  const plan = framework ? alliedPlanFor(framework) : null;
  // [PROD-3376] The template draws the nutrition care process itself → only the spine, no picker.
  // Saved PES/ADIME text keeps the card (and its picker), so nothing written is ever hidden.
  const templateOwnsFramework =
    (frameworkInTemplate && framework === 'pes-adime' && !hasSavedPesAdimeText(values)) ||
    (templateOwnsPlan && !hasSavedFrameworkText(values));
  const showPicker = needsPick && !templateOwnsFramework;
  // With no framework yet the spine still renders: it is discipline-independent, so the clinician
  // can start the plan while deciding. Only the framework block waits on the pick.
  // [PROD-3376] With the template's own Plan sections, the Wrap-up "Follow-Up" (which books the next visit at sign)
  // already says when the patient returns; the spine's free-text review interval would ask it again (second-model
  // review, 2 Nov). Dropped then — unless it already holds saved text, which is never hidden.
  const reviewSaved = (values?.['plan-review-interval'] ?? '').trim() !== '';
  const shownSpine =
    templateOwnsPlan && !reviewSaved ? { ...spine, fields: spine.fields.filter((f) => f.id !== 'plan-review-interval') } : spine;
  const sections: AlliedPlanSection[] = plan && !templateOwnsFramework ? planSections(plan) : [shownSpine];

  return (
    <div
      className={WRAP}
      // `data-terminal` is the machine-readable statement that this step ends in a PLAN. The
      // no-order-affordance test asserts on it, so the guarantee is checked against the rendered
      // DOM rather than only against the section data.
      data-testid="allied-health-plan"
      data-terminal={ALLIED_PLAN_TERMINAL}
    >
      {showPicker && (
        <PlanCard
          titleId="allied-plan-framework-title"
          title="Framework"
          subtitle={PICK_PROMPT[resolution.reason]}
          // The plan's status lives in its FIRST card: this one, while the picker shows.
          notice={<PlanSaveNotice state={saveState} />}
          headerEnd={<PlanSaveLine state={saveState} />}
          data-testid="allied-plan-framework-picker"
          inNote={inNote}
          // The two reasons are distinguishable from the DOM, so a mis-seeded speciality is
          // observable in a test and in a screenshot rather than looking like the normal case.
          data-pick-reason={resolution.reason}
        >
          <RadioGroup<AlliedPlanFramework>
            name="allied-plan-framework"
            options={options}
            value={picked ?? undefined}
            onChange={setPicked}
            orientation="vertical"
            disabled={locked}
          />
        </PlanCard>
      )}

      {sections.map((section, i) => (
        <PlanSectionBlock
          key={section.id}
          section={section}
          values={values}
          onChange={onChange}
          locked={locked}
          inNote={inNote}
          // In the note the block is named for the spine ("Today's plan"), so the spine draws no title.
          untitled={inNote && section.id === shownSpine.id}
          {...(!showPicker && i === 0
            ? { notice: <PlanSaveNotice state={saveState} />, headerEnd: <PlanSaveLine state={saveState} /> }
            : {})}
        />
      ))}
    </div>
  );
}
